import { useCallback, useEffect, useState } from "react";
import {
  DIETARY_TYPES,
  FOOD_TYPES,
  MACROS_BASE_TYPES,
  S3,
} from "../../../../../../constants";
import ImageWidgetUploadInput from "../../../../../../parts/inputs/image-widget-upload";
import NutrientInput from "../../../../../../parts/inputs/nutrient-input";
import { RadioMultipleVerticalGroup } from "../../../../../../parts/inputs/radio-multiple-vertical-group";
import RadioVerticalGroup from "../../../../../../parts/inputs/radio-vertical-group";
import NutrientsChartForWidget from "../../../../../athlete-portal/nutrition/components/NutrientsChartForWidget";
import scriptLoader from "../../../../../../utils/scriptLoader";
import submit from "submit-form";
import SvgIcon from "../../../../../../parts/SvgIcon";
import Dropdown from "../../../../../../parts/dropdown";
import ToolTip from "../../../../../../parts/tooltip";

export const EditRecipe = ({
  idProp,
  imageProp = {},
  nameProp,
  statusProp,
  nutrientsProp,
  prepTimeProp,
  difficultyProp = "easy",
  foodTypeProp = "breakfast",
  dietaryRestrictionsProp = [""],
  ingredientsProp = [],
  stepsProp = [],
  servingsProp,
  descriptionProp,
  macrosBaseProp = "serving",
  hasImage,
  handleRenderStateChange,
  locals,
}) => {
  //callback to close the accordion
  const handleInitialState = useCallback(
    (initial) => {
      if (!!handleRenderStateChange) {
        handleRenderStateChange(initial);
      }
    },
    [handleRenderStateChange]
  );

  const [tabState, setTabState] = useState("information-tab"); //state to control de active tab

  //state to control the steps of the instruction's form
  const [inputInstructionsFields, setInputInstructionsFields] = useState({
    description: descriptionProp,
    steps: [{ step: "" }],
  });
  //state to control the steps of the ingredient's form
  const [inputIngredientsFields, setInputIngredientsFields] = useState({
    servings: servingsProp,
    steps: [{ ingredient: "", qty: "" }],
  });

  //state to control the rest inputs of the forms (all of them except instructions. dietary state and ingredients fields)
  const [formState, setFormState] = useState({
    name: nameProp ? nameProp : "",
    prepTime: prepTimeProp ? prepTimeProp : "",
    difficulty: difficultyProp ? difficultyProp : "",
    foodType: foodTypeProp ? foodTypeProp : "",
    macrosBase: macrosBaseProp ? macrosBaseProp : "",
    fibers: nutrientsProp ? nutrientsProp.fibers : "",
    fats: nutrientsProp ? nutrientsProp.fats : "",
    carbs: nutrientsProp ? nutrientsProp.carbs : "",
    proteins: nutrientsProp ? nutrientsProp.proteins : "",
  });
  //state to control the dietary state value
  const initialDietaryState =
    dietaryRestrictionsProp === null ? [""] : dietaryRestrictionsProp;
  const [dietaryState, setDietaryState] = useState(initialDietaryState);
  const [imageState, setImageState] = useState(null); //state to know if an image was attached

  const {
    name,
    prepTime,
    difficulty,
    foodType,
    macrosBase,
    fibers,
    fats,
    carbs,
    proteins,
  } = formState;

  /**
   * Delete a recipe
   * @param {*} e
   */
  const handleDelete = (e) => {
    if (
      process.browser &&
      confirm("Are you sure you want to delete this recipe?")
    ) {
      e.stopPropagation();
      e.preventDefault();
      handleInitialState(
        Object.assign(
          {},
          {
            isOpen: false,
            recipe: idProp,
          }
        )
      );
      submit(`/actions/recipes/delete/${idProp}`, {
        method: "GET",
      });
    }
  };

  /**
   * Assign value for the formState
   * @param {*} param0
   */
  const handleInputChange = ({ target }) => {
    const newState = Object.assign({}, formState, {
      [target.name]:
        target.name === "name" ||
        target.name === "prepTime" ||
        target.name === "foodType" ||
        target.name === "macrosBase" ||
        target.name === "difficulty"
          ? target.value
          : parseInt(target.value),
    });
    setFormState(newState);
  };

  /**
   * Change div's content
   * @param {*} tab
   */
  const handleActiveTab = (tab) => {
    setTabState(tab);
  };

  /**
   * Asign the instructions and ingredients steps to the states
   */
  useEffect(() => {
    const instructionsStepArray = [];
    const insgredientsStepArray = [];
    if (!stepsProp || stepsProp.length === 0) {
      instructionsStepArray.push({ step: "" });
    } else {
      stepsProp.forEach((element, index) => {
        const step = {
          step: element,
          order: index + 1,
        };
        instructionsStepArray.push(step);
      });
    }
    if (!ingredientsProp || ingredientsProp.length === 0) {
      insgredientsStepArray.push({ ingredient: "", qty: "" });
    } else {
      ingredientsProp.forEach((element, index) => {
        const step = {
          ingredient: element.name,
          order: index + 1,
          qty: element.quantity,
        };
        insgredientsStepArray.push(step);
      });
    }

    setInputInstructionsFields(
      Object.assign({}, inputInstructionsFields, {
        steps: instructionsStepArray,
      })
    );

    setInputIngredientsFields(
      Object.assign({}, inputIngredientsFields, {
        steps: insgredientsStepArray,
      })
    );
  }, []);

  /**
   * Reorder the ingredients
   */
  useEffect(() => {
    if (document.querySelector(".steps-recipe-ingredients")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".steps-recipe-ingredients"),
            {
              animation: 350,
              handle: ".drag-handle-ingredient",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                // this will override sortable's default DOM manipulation
                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                // reorder the ingredients state, this will determine the new DOM order
                // since we stopped sortable's default behavior
                const servingsInput = document.querySelector(
                  ".servings-input-instruction-grab"
                );

                const reorderedIngredients = Array.from(items).map(
                  (element) => {
                    const nameInput = element.querySelector(
                      ".name-input-ingredient-grab"
                    );
                    const quantityInput = element.querySelector(
                      ".quantity-input-ingredient-grab"
                    );
                    return {
                      ingredient: (nameInput && nameInput.value) || "",
                      qty: (quantityInput && quantityInput.value) || "",
                    };
                  }
                );

                setInputIngredientsFields({
                  ...inputIngredientsFields,
                  steps: reorderedIngredients,
                });
              },
            }
          );
        }
      );
    }
  }, [tabState]);

  /**
   * Reorder the instructions steps
   */
  useEffect(() => {
    if (document.querySelector(".steps-recipe-instructions")) {
      scriptLoader(
        "https://cdn.jsdelivr.net/npm/sortablejs@latest/Sortable.min.js",
        () => {
          const dragImage = document.createElement("img");
          dragImage.src =
            "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7";
          const sortable = new Sortable(
            document.querySelector(".steps-recipe-instructions"),
            {
              animation: 350,
              handle: ".drag-handle-steps",
              setData(dataTransfer) {
                dataTransfer.setDragImage(dragImage, 0, 0);
              },
              onEnd: (e) => {
                if (e.oldIndex === e.newIndex) return;

                // this will override sortable's default DOM manipulation
                const items = e.from.querySelectorAll(":scope > div");
                e.from.insertBefore(
                  e.item,
                  items[e.oldIndex + (e.oldIndex > e.newIndex)]
                );

                // reorder the ingredients state, this will determine the new DOM order
                // since we stopped sortable's default behavior
                const descriptionInput = document.querySelector(
                  ".description-input-instruction-grab"
                );
                const reorderedInstructions = Array.from(items).map(
                  (element) => {
                    const nameInput = element.querySelector(
                      ".name-input-instruction-grab"
                    );
                    return {
                      step: (nameInput && nameInput.value) || "",
                    };
                  }
                );

                setInputInstructionsFields({
                  ...inputInstructionsFields,
                  steps: reorderedInstructions,
                });
              },
            }
          );
        }
      );
    }
  }, [tabState]);

  /**
   * assign the value for the steps of the instruction's state
   * @param {*} index
   * @param {*} event
   */
  const handleFormInstructionsChange = (index, event) => {
    let data = JSON.parse(JSON.stringify(inputInstructionsFields.steps));
    data[index][event.target.name] = event.target.value;
    const newState = Object.assign({}, inputInstructionsFields, {
      steps: data,
    });
    setInputInstructionsFields(newState);
  };

  /**
   * Create more inputs in the steps of the instruction's steps
   */
  const addInstructionsFields = () => {
    let newInstructionfield = { step: "" };

    let data = JSON.parse(JSON.stringify(inputInstructionsFields.steps));
    data.push(newInstructionfield);
    const newState = Object.assign({}, inputInstructionsFields, {
      steps: data,
    });
    setInputInstructionsFields(newState);
  };

  /**
   * remove inputs in the steps of the instruction's steps
   * @param {*} index
   */
  const removeInstructionsFields = (index, e) => {
    if (confirm("Are you sure you want to delete this step?")) {
      e.preventDefault();
      let data = JSON.parse(JSON.stringify(inputInstructionsFields.steps));
      data.splice(index, 1);
      const newState = Object.assign({}, inputInstructionsFields, {
        steps: data,
      });
      setInputInstructionsFields(newState);
    }
  };

  /**
   * assign the value for the steps of the ingredient's state
   * @param {*} index
   * @param {*} event
   */
  const handleFormIngredientsChange = (index, event) => {
    let newState;
    if (index !== null) {
      let data = JSON.parse(JSON.stringify(inputIngredientsFields.steps));
      data[index][event.target.name] = event.target.value;
      newState = Object.assign({}, inputIngredientsFields, {
        steps: data,
      });
    } else {
      newState = Object.assign({}, inputIngredientsFields, {
        servings: parseInt(event.target.value),
      });
    }

    setInputIngredientsFields(newState);
  };

  /**
   * Create more inputs in the steps of the ingredient's steps
   */
  const addIngredientsFields = () => {
    let newInstructionfield = { ingredient: "", qty: "" };

    let data = JSON.parse(JSON.stringify(inputIngredientsFields.steps));
    data.push(newInstructionfield);
    const newState = Object.assign({}, inputIngredientsFields, {
      steps: data,
    });
    setInputIngredientsFields(newState);
  };

  /**
   * remove inputs in the steps of the ingredient's steps
   * @param {*} index
   */
  const removeIngredientsFields = (index, e) => {
    if (confirm("Are you sure you want to delete this step?")) {
      e.preventDefault();
      let data = JSON.parse(JSON.stringify(inputIngredientsFields.steps));
      data.splice(index, 1);
      const newState = Object.assign({}, inputIngredientsFields, {
        steps: data,
      });
      setInputIngredientsFields(newState);
    }
  };

  /**
   * assign the value for the description of the instruction's state
   * @param {*} param0
   */
  const handleFormInstructionsDescriptionChange = ({ target }) => {
    const newState = Object.assign({}, inputInstructionsFields, {
      description: target.value,
    });
    setInputInstructionsFields(newState);
  };
  const handleNextTab = (e) => {
    e.preventDefault();
    if (tabState === "information-tab") setTabState("ingredients-tab");
    if (tabState === "ingredients-tab") setTabState("instructions-tab");
    if (tabState === "instructions-tab") setTabState("nutrition-tab");
  };

  /**
   * Validate if edit form is valid
   * @returns
   */

  const validationFormSubmit = () => {
    const isNameValid = formState?.name?.length > 0;
    const isImageValid = imageState !== null || !!imageProp?.s3Path;

    const message = !isNameValid
      ? "The name of the recipe is required"
      : !isImageValid
      ? "The image of the recipe is required"
      : "";

    return { isValid: isNameValid && isImageValid, message };
  };

  const handleImageUpload = (newImage) => {
    setImageState(newImage);
  };

  return (
    <div className="main-tabs-recipe mobile-hidden">
      <header>
        <nav
          role="tablist"
          id="tabsRecipeSection"
          className={idProp ? "nav-justify-normal" : "nav-justify-around"}
        >
          <button
            id="information-tab"
            onClick={() => handleActiveTab("information-tab")}
            aria-current={tabState === "information-tab" ? true : undefined}
          >
            Information
          </button>
          <button
            id="ingredients-tab"
            onClick={() => handleActiveTab("ingredients-tab")}
            aria-current={tabState === "ingredients-tab" ? true : undefined}
          >
            Ingredients
          </button>
          <button
            id="instructions-tab"
            aria-current={tabState === "instructions-tab" ? true : undefined}
            onClick={() => handleActiveTab("instructions-tab")}
          >
            Instructions
          </button>
          <button
            id="nutrition-tab"
            onClick={() => handleActiveTab("nutrition-tab")}
            aria-current={tabState === "nutrition-tab" ? true : undefined}
          >
            Nutrition
          </button>
          {idProp && (
            <button
              onClick={handleDelete}
              data-button-type="secondary"
              data-action="cancel"
            >
              Delete
            </button>
          )}
        </nav>
      </header>
      <form
        encType="multipart/form-data"
        method="POST"
        data-noreset
        action={
          idProp
            ? `/actions/recipes/save/${idProp}`
            : `/actions/recipes/create/`
        }
        onSubmit={() => handleInitialState({ isOpen: false })}
      >
        {/* information tab */}
        <div
          className={
            tabState === "information-tab" ? "information-tab" : "hidden"
          }
        >
          <div className="flex gap-6">
            <div className="w-[340px]">
              <div>
                <label> Recipe Name:</label>
                <input
                  name="name"
                  className="text-input"
                  placeholder={`Name`}
                  value={name}
                  onChange={handleInputChange}
                />
              </div>
              <div>
                <div>
                  <label> Prep Time </label>
                  <input
                    name="prepTime"
                    className="text-input"
                    placeholder={`Prep Time`}
                    value={prepTime}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </div>
            <ImageWidgetUploadInput
              name="image"
              prefix="Video playlist"
              defaultValue={imageProp}
              backgroundedit={`${S3.PATH}${imageProp.s3Path}`}
              hasImage={hasImage}
              handleImageUpload={handleImageUpload}
            />
          </div>
          <div className="w-[340px]">
            <label> Difficulty </label>
            <Dropdown
              summaryText={
                difficulty.charAt(0).toUpperCase() + difficulty.substring(1)
              }
              options={[
                { text: "Easy", value: "easy" },
                { text: "Medium", value: "medium" },
                { text: "Hard", value: "hard" },
              ]}
              onSelectFn={(value) => {
                setFormState({ ...formState, difficulty: value });
              }}
            />
            <input name="difficulty" defaultValue={difficulty} type="hidden" />
          </div>
          <div className="flex gap-6">
            <RadioVerticalGroup
              label="Food type"
              name="foodType"
              currentValue={foodType}
              radios={FOOD_TYPES}
              onChange={handleInputChange}
            />
            <RadioMultipleVerticalGroup
              label="Dietary Restrictions"
              name="dietary"
              radios={DIETARY_TYPES}
              currentValue={dietaryState}
              handleStateChange={setDietaryState}
            />
            <input
              name="dietaryRestrictions"
              style={{ display: "none" }}
              readOnly
              value={dietaryState}
            />
            {!idProp && (
              <input
                name="widget"
                style={{ display: "none" }}
                readOnly
                value={locals.query.widget}
              />
            )}
          </div>
        </div>
        {/* ingredients tab */}
        <div
          className={
            tabState === "ingredients-tab" ? "ingredients-tab" : "hidden"
          }
        >
          <div>
            <label> Number of servings </label>
            <input
              type="number"
              name="servings"
              value={inputIngredientsFields.servings}
              onChange={(event) => handleFormIngredientsChange(null, event)}
            />
          </div>
          <section>
            <header>
              <span className="steps-title"></span>

              <label>Quantity</label>
              <label>Ingredient name</label>
            </header>

            <div className="steps-recipe-ingredients">
              {inputIngredientsFields.steps.map((input, index) => {
                return (
                  <div key={index} className="item-card-container" id={index}>
                    <span>{index + 1}.</span>

                    <input
                      name="qty"
                      className="text-input quantity-input-ingredient-grab"
                      placeholder={`Quantity`}
                      value={input.qty}
                      onChange={(event) =>
                        handleFormIngredientsChange(index, event)
                      }
                    />
                    <input
                      name="ingredient"
                      className="text-input name-input-ingredient-grab"
                      placeholder={`Name`}
                      value={input.ingredient}
                      onChange={(event) =>
                        handleFormIngredientsChange(index, event)
                      }
                    />
                    <div className="action-handlers">
                      <button
                        onClick={() => removeIngredientsFields(index)}
                        data-button-type="secondary"
                        data-icon
                        className="drag-handle-ingredient"
                      >
                        <SvgIcon src="/img/new-icons/menu.svg" />
                      </button>
                      <button
                        onClick={(e) => {
                          removeIngredientsFields(index, e);
                        }}
                        data-button-type="secondary"
                        data-icon
                      >
                        <SvgIcon src="/img/new-icons/red-trash.svg" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <footer>
            <button
              type="button"
              data-button-type="outline"
              onClick={addIngredientsFields}
            >
              <span>Add New Ingredient</span>
            </button>
          </footer>
        </div>
        {/* instruction tab */}
        <div
          className={
            tabState === "instructions-tab" ? "instructions-tab" : "hidden"
          }
        >
          <header>
            <label> Description </label>
            <textarea
              placeholder="Enter description here.."
              value={inputInstructionsFields.description}
              onChange={handleFormInstructionsDescriptionChange}
              name="description"
            />
          </header>
          <section>
            <p className="p4 mb-2">Steps</p>
            <div className="steps-recipe-instructions">
              {inputInstructionsFields.steps.map((input, index) => {
                return (
                  <div
                    key={index}
                    className="item-card-container step-all-section"
                    id={index}
                  >
                    <span>{index + 1}.</span>

                    <input
                      name="step"
                      className="text-input name-input-instruction-grab"
                      placeholder={`Name`}
                      value={input.step}
                      onChange={(event) =>
                        handleFormInstructionsChange(index, event)
                      }
                    />

                    <div className="action-handlers">
                      <button
                        data-button-type="secondary"
                        data-icon
                        className="drag-handle-steps"
                      >
                        <SvgIcon src="/img/new-icons/menu.svg" />
                      </button>
                      <button
                        data-button-type="secondary"
                        data-icon
                        onClick={(e) => removeInstructionsFields(index, e)}
                      >
                        <SvgIcon src="/img/new-icons/red-trash.svg" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
          <footer>
            <button
              type="button"
              data-button-type="outline"
              onClick={addInstructionsFields}
            >
              <span>Add New Step</span>
            </button>
          </footer>
        </div>
        {/* nutrition tab */}
        <div
          className={tabState === "nutrition-tab" ? "nutrition-tab" : "hidden"}
        >
          <section className="w-[340px]">
            <RadioVerticalGroup
              label="Macros based on"
              name="macrosBase"
              currentValue={macrosBase}
              radios={MACROS_BASE_TYPES}
              onChange={handleInputChange}
            />
            <label className="label protein req" htmlFor="proteins">
              Protein (1g = 4kcal)
            </label>
            <NutrientInput
              widgetStyle={true}
              name="proteins"
              suffix="g"
              value={proteins}
              onChange={handleInputChange}
            />
            <label className="label carbs req" htmlFor="carbs">
              Carbs (1g = 4kcal)
            </label>
            <NutrientInput
              widgetStyle={true}
              name="carbs"
              suffix="g"
              value={carbs}
              onChange={handleInputChange}
            />
            <label className="label fat req" htmlFor="fats">
              Fat (1g = 9kcal)
            </label>
            <NutrientInput
              widgetStyle={true}
              name="fats"
              suffix="g"
              value={fats}
              onChange={handleInputChange}
            />
            <label className="label fiber" htmlFor="fibers">
              Fiber (Optional)
            </label>
            <NutrientInput
              widgetStyle={true}
              name="fibers"
              suffix="g"
              value={fibers}
              onChange={handleInputChange}
            />
          </section>

          {tabState === "nutrition-tab" && (
            <NutrientsChartForWidget
              dayStats={{
                nutrients: {
                  proteins: proteins || 0,
                  carbs: carbs || 0,
                  fats: fats || 0,
                  fibers: fibers || 0,
                },
              }}
            />
          )}
        </div>
        <footer>
          {tabState === "nutrition-tab" ? (
            <ToolTip
              id="save-recipe"
              body={validationFormSubmit().message}
              className="recipe-tooltip-container"
            >
              <button
                data-button-type="primary"
                type="submit"
                className="save-macro-btn"
                disabled={!validationFormSubmit().isValid}
              >
                Save
              </button>
            </ToolTip>
          ) : (
            <button data-button-type="primary" onClick={handleNextTab}>
              Next
            </button>
          )}
        </footer>
      </form>
    </div>
  );
};
